import { Injectable } from '@angular/core';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { CuratedLesson, SourceResponse } from '../models/response-open-search';
import { CellData } from '../models/CellData';

@Injectable({ providedIn: 'root' })
export class ExportService {

  exportTableToExcel(tableIndex: number): void {
    const wrappers = document.querySelectorAll('.table-export-wrapper');
    const wrapper = wrappers[tableIndex] as HTMLElement | undefined;
    const table = wrapper?.querySelector('table') as HTMLTableElement | undefined;
    if (!table) return;
    const anchorRefMap = this.extractRefLinksFromHtmlTable(table.outerHTML);
    const rawRows: CellData[][] = Array.from(table.querySelectorAll('tr')).map((row, rowIdx) =>
      Array.from(row.querySelectorAll('th, td')).map(cell => {
        const anchors = Array.from(cell.querySelectorAll('a'));
        const cellText = cell.textContent?.trim() ?? '';
        if (anchors.length > 1) {
          const primary: CellData & { _extraUrls?: { num: string; url: string }[] } = {
            text: cellText,
            url: anchors[0].getAttribute('href') ?? null,
            isHeader: rowIdx === 0,
            _extraUrls: anchors.slice(1).map((a, i) => ({
              num: String(i + 2),
              url: a.getAttribute('href') ?? '',
            })),
          };
          return primary as CellData;
        }
        if (anchors.length === 1) {
          return { text: cellText, url: anchors[0].getAttribute('href') ?? null, isHeader: rowIdx === 0 };
        }
        const m = /^\s*\[?(\d+)\]?\s*$/.exec(cellText);
        return { text: cellText, url: m ? (anchorRefMap.get(m[1]) ?? null) : null, isHeader: rowIdx === 0 };
      }),
    );
    const expandedRows = rawRows.map(row => this.expandMultiUrlCells(row));
    this.downloadBlob(this.zipToBlob(this.buildXlsxFiles(expandedRows)), `tabla_${tableIndex + 1}.xlsx`);
  }

  exportResponseToExcel(
    interactionIndex: number,
    markdown: string,
    sources: SourceResponse[],
    lessons: CuratedLesson[] = [],
  ): void {
    if (!markdown) return;
    const withoutRefs = this.removeReferencesBlock(markdown);
    const lessonsMarkdown = this.curatedLessonsToMarkdown(lessons);
    const withLinks = this.injectLinksIntoMarkdown(withoutRefs + lessonsMarkdown, sources);
    const cleaned = withLinks.replace(/<!--[\s\S]*?-->/g, '').trim();
    const sections = this.parseMarkdownIntoSections(cleaned, sources);
    const allRows = this.flattenSectionsToRows(sections);
    this.downloadBlob(this.zipToBlob(this.buildXlsxFiles(allRows)), `respuesta_${interactionIndex + 1}.xlsx`);
  }

  exportResponseToDoc(interactionIndex: number, markdown: string, lessons: CuratedLesson[] = []): void {
    if (!markdown) return;
    const rawHtml = marked(markdown + this.curatedLessonsToMarkdown(lessons)) as string;
    const cleanHtml = DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS: ['p', 'br', 'b', 'strong', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'span', 'a'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
      FORCE_BODY: true,
    });
    const docHtml =
      '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">' +
      '<head><meta charset="utf-8"/><meta name=ProgId content=Word.Document/><meta name=Generator content="Microsoft Word 15"/>' +
      '<style>body{font-family:Calibri,Arial,sans-serif;font-size:11pt;color:#1A1A2E;margin:0;padding:0;line-height:1.5}' +
      'h1,h2,h3{color:#2D4A6B;font-weight:bold;margin-top:14pt}h1{font-size:18pt}h2{font-size:15pt}h3{font-size:13pt}' +
      'h4,h5{font-size:11pt;font-weight:bold;color:#2D4A6B}p{margin:0 0 8pt 0}strong{font-weight:bold}em{font-style:italic}' +
      'ul,ol{margin:0 0 8pt 0;padding-left:1.2cm}li{margin-bottom:4pt}a{color:#1A5EAF}' +
      'table{border-collapse:collapse;width:100%;margin-bottom:12pt;font-size:10pt}' +
      'th{background-color:#E8EDF5;color:#2D4A6B;font-weight:bold;border:1pt solid #E0E3EA;padding:5pt 8pt;text-align:left;vertical-align:top}' +
      'td{border:1pt solid #E0E3EA;padding:5pt 8pt;vertical-align:top;text-align:left}' +
      'tr:nth-child(even) td{background-color:#F5F5F8}</style></head>' +
      `<body>${cleanHtml}</body></html>`;
    this.downloadBlob(new Blob(['\uFEFF' + docHtml], { type: 'application/msword;charset=utf-8;' }), `respuesta_${interactionIndex + 1}.doc`);
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = filename;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  private escapeXml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
  }

  private extractRefLinksFromHtmlTable(tableHtml: string): Map<string, string> {
    const map = new Map<string, string>();
    const re = /<a[^>]+href="([^"]+)"[^>]*>\[?(\d+)\]?<\/a>/gi;
    let m: RegExpExecArray | null;
    while ((m = re.exec(tableHtml)) !== null) map.set(m[2], m[1]);
    return map;
  }

  private removeReferencesBlock(markdown: string): string {
    const refBlockStart = markdown.search(/^\s*\[\d+\]:\s*https?:\/\//m);
    if (refBlockStart !== -1) {
      return markdown.slice(0, refBlockStart).trimEnd();
    }
    return markdown;
  }

  private injectLinksIntoMarkdown(text: string, sources: SourceResponse[]): string {
    return text.replace(/\[(\d+)\]/g, (_, num) => {
      const src = sources[parseInt(num, 10) - 1];
      return src ? `[${num}](${src.url})` : `[${num}]`;
    });
  }

  private curatedLessonsToMarkdown(lessons: CuratedLesson[]): string {
    if (!lessons.length) return '';
    const lines = ['\n\n## Lecciones curadas relevantes\n'];
    lessons.forEach(lesson => {
      const title = this.lessonValue(lesson, ['title', 'lessonName', 'name']);
      const codes = this.toStringArray(lesson.operation_code ?? lesson.operationCode ?? lesson.operationCodes);
      const titleWithCodes = `${title}${codes.length ? ` (${codes.join(', ')})` : ''}`.trim();
      if (titleWithCodes) lines.push(`### ${titleWithCodes}\n`);

      const text = this.lessonValue(lesson, ['lesson', 'description']);
      if (text) {
        const parts = this.splitLessonText(text);
        if (parts.finding) lines.push(`- Hallazgo: ${parts.finding}\n`);
        if (parts.recommendation) lines.push(`- Recomendación: ${parts.recommendation}\n`);
        if (!parts.finding && !parts.recommendation) lines.push(`${text}\n`);
      } else {
        const finding = this.lessonValue(lesson, ['finding', 'hallazgo']);
        const recommendation = this.lessonValue(lesson, ['recommendation', 'recomendacion']);
        if (finding) lines.push(`- Hallazgo: ${finding}\n`);
        if (recommendation) lines.push(`- Recomendación: ${recommendation}\n`);
      }

      this.lessonSources(lesson).forEach(source => {
        lines.push(`[${source.fileName}](${source.url})\n`);
      });
      lines.push('\n');
    });
    return lines.join('');
  }

  private lessonValue(lesson: CuratedLesson, keys: string[]): string {
    for (const key of keys) {
      const value = lesson[key];
      if (typeof value === 'string' && value.trim()) return value;
    }
    return '';
  }

  private toStringArray(value: unknown): string[] {
    if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string');
    return typeof value === 'string' && value.trim() ? [value] : [];
  }

  private splitLessonText(text: string): { finding: string; recommendation: string } {
    const findingMatch = /\b(?:hallazgo|finding|achado|constata(?:ção|cion|ción|t)|constat)\s*:\s*/i.exec(text);
    const recommendationMatch = /\b(?:recomendaci(?:ón|on)|recommendation|recomenda(?:ção|cion|ción)|recommandation)\s*:\s*/i.exec(text);
    return {
      finding: findingMatch
        ? text.slice(findingMatch.index + findingMatch[0].length, recommendationMatch?.index ?? text.length).trim()
        : '',
      recommendation: recommendationMatch
        ? text.slice(recommendationMatch.index + recommendationMatch[0].length).trim()
        : '',
    };
  }

  private lessonSources(lesson: CuratedLesson): { fileName: string; url: string }[] {
    const urls = this.toStringArray(lesson.url);
    const fileNames = this.toStringArray(lesson.file_name ?? lesson.fileName);
    return urls
      .map((url, index) => ({ url, fileName: fileNames[index] ?? url }))
      .filter(source => /^https?:\/\//i.test(source.url));
  }

  private parseMarkdownIntoSections(
    markdown: string,
    sources: SourceResponse[],
  ): Array<{ type: 'text'; content: string } | { type: 'table'; rows: CellData[][] }> {
    const sections: Array<{ type: 'text'; content: string } | { type: 'table'; rows: CellData[][] }> = [];
    const tableRegex = /((?:\|.+\|[ \t]*\n?)+)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = tableRegex.exec(markdown)) !== null) {
      const before = markdown.slice(lastIndex, match.index).trim();
      if (before) {
        const cleaned = this.stripNarrativeText(before);
        if (cleaned) sections.push({ type: 'text', content: cleaned });
      }
      const lines = match[0].trim().split('\n')
        .map(l => l.trim())
        .filter(l => l && !/^\|[\s\-|:]+\|$/.test(l));
      const rows: CellData[][] = lines.map((line, rowIdx) => {
        const rawCells = line.split('|').slice(1, -1).map(c => c.trim());
        return rawCells.map(cellText => this.parseCellWithLinks(cellText, rowIdx === 0, sources));
      });
      sections.push({ type: 'table', rows });
      lastIndex = match.index + match[0].length;
    }
    const remaining = markdown.slice(lastIndex).trim();
    if (remaining) {
      const cleaned = this.stripNarrativeText(remaining);
      if (cleaned) sections.push({ type: 'text', content: cleaned });
    }
    return sections;
  }

  private stripNarrativeText(text: string): string {
    return text
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/\[([^\]\d][^\]]*)\]\([^)]+\)/g, '$1')
      .replace(/^[-*]\s+/gm, '• ')
      .replace(/^\d+\.\s+/gm, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  private parseCellWithLinks(cellText: string, isHeader: boolean, sources: SourceResponse[]): CellData {
    const linkRe = /\[(\d+)\]\(([^)]+)\)/g;
    const found: { num: string; url: string }[] = [];
    let m: RegExpExecArray | null;
    while ((m = linkRe.exec(cellText)) !== null) {
      found.push({ num: m[1], url: m[2] });
    }
    if (found.length > 0) {
      const narrativeOnly = cellText
        .replace(/\[(\d+)\]\([^)]+\)/g, '')
        .replace(/^#{1,6}\s+/gm, '')
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/\*(.*?)\*/g, '$1')
        .replace(/\s{2,}/g, ' ')
        .trim();
      const cell: CellData & { _extraUrls?: { num: string; url: string }[] } = {
        text: narrativeOnly || `[${found[0].num}]`,
        url: found[0].url,
        isHeader,
      };
      const extraRefs = narrativeOnly ? found : found.slice(1);
      if (extraRefs.length > 0) cell._extraUrls = extraRefs;
      return cell;
    }
    const plainRef = /^\s*\[(\d+)\]\s*$/.exec(cellText);
    if (plainRef) {
      const src = sources[parseInt(plainRef[1], 10) - 1];
      return { text: `[${plainRef[1]}]`, url: src?.url ?? null, isHeader };
    }
    return {
      text: cellText.replace(/^#{1,6}\s+/gm, '').replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1').trim(),
      url: null,
      isHeader,
    };
  }

  private flattenSectionsToRows(
    sections: Array<{ type: 'text'; content: string } | { type: 'table'; rows: CellData[][] }>,
  ): CellData[][] {
    type CellExt = CellData & { _extraUrls?: { num: string; url: string }[] };
    const expandLine = (line: string): CellData[] => {
      const cell = this.parseCellWithLinks(line, false, []) as CellExt;
      return this.expandMultiUrlCells([cell]);
    };
    type ExpandedSec =
      | { type: 'textRows'; rows: CellData[][] }
      | { type: 'table'; rows: CellData[][] };
    const expanded: ExpandedSec[] = sections.map(s => {
      if (s.type === 'text') {
        const rows = s.content.split('\n')
          .filter(l => l.trim())
          .map(line => expandLine(line));
        return { type: 'textRows', rows };
      }
      return { type: 'table', rows: s.rows.map(r => this.expandMultiUrlCells(r)) };
    });
    let maxCols = 1;
    for (const s of expanded) {
      const cols = Math.max(...s.rows.map(r => r.length));
      if (cols > maxCols) maxCols = cols;
    }
    const empty = (): CellData[] =>
      Array.from({ length: maxCols }, () => ({ text: '', url: null, isHeader: false }));
    const allRows: CellData[][] = [];
    for (const section of expanded) {
      for (const row of section.rows) {
        const padded = [...row];
        while (padded.length < maxCols) padded.push({ text: '', url: null, isHeader: false });
        allRows.push(padded);
      }
      allRows.push(empty());
    }
    return allRows;
  }

  private expandMultiUrlCells(row: CellData[]): CellData[] {
    const result: CellData[] = [];
    for (const cell of row) {
      const extra = (cell as CellData & { _extraUrls?: { num: string; url: string }[] })._extraUrls;
      result.push({ text: cell.text, url: cell.url, isHeader: cell.isHeader });
      if (extra) {
        for (const e of extra) {
          result.push({ text: `[${e.num}]`, url: e.url, isHeader: cell.isHeader });
        }
      }
    }
    return result;
  }

  private buildXlsxFiles(rows: CellData[][]): Record<string, string> {
    const strings: string[] = [];
    const strIndex = new Map<string, number>();
    const si = (val: string): number => {
      if (!strIndex.has(val)) { strIndex.set(val, strings.length); strings.push(val); }
      return strIndex.get(val)!;
    };
    rows.forEach(row => row.forEach(cell => si(cell.text)));
    const colCount = Math.max(...rows.map(r => r.length), 1);
    const colWidths = Array.from({ length: colCount }, (_, ci) => {
      const maxLen = Math.max(...rows.map(r => (r[ci]?.text ?? '').length));
      return Math.min(80, Math.max(8, maxLen + 2));
    });
    const colDefs = colWidths
      .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
      .join('');
    const hyperlinkRels: { id: string; url: string }[] = [];
    const cellRelMap = new Map<string, string>();
    rows.forEach((row, ri) => {
      row.forEach((cell, ci) => {
        if (cell.url && /^https?:\/\//i.test(cell.url)) {
          const relId = `rHyp${ri}_${ci}`;
          hyperlinkRels.push({ id: relId, url: cell.url });
          cellRelMap.set(`${ri}_${ci}`, relId);
        }
      });
    });
    const hyperlinksXml = hyperlinkRels.length
      ? '<hyperlinks>' +
      rows.flatMap((row, ri) =>
        row.map((_, ci) => {
          const relId = cellRelMap.get(`${ri}_${ci}`);
          return relId ? `<hyperlink ref="${this.colLetter(ci)}${ri + 1}" r:id="${relId}"/>` : '';
        }),
      ).join('') + '</hyperlinks>'
      : '';
    const sheetRows = rows.map((row, ri) => {
      const isHeaderRow = row[0]?.isHeader ?? false;
      const isNarrativeRow = row[0]?.text !== '' && row.slice(1).every(c => c.text === '');
      const cells = row.map((cell, ci) => {
        const hasLink = cellRelMap.has(`${ri}_${ci}`);
        let s: number;
        if (isHeaderRow) s = 0;
        else if (hasLink) s = 3;
        else if (isNarrativeRow && ci === 0) s = 4;
        else s = ri % 2 === 0 ? 1 : 2;
        return `<c r="${this.colLetter(ci)}${ri + 1}" t="s" s="${s}"><v>${si(cell.text)}</v></c>`;
      }).join('');
      return `<row r="${ri + 1}">${cells}</row>`;
    }).join('');
    const sheetXml =
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"' +
      ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
      '<sheetViews><sheetView workbookViewId="0"/></sheetViews>' +
      `<cols>${colDefs}</cols>` +
      `<sheetData>${sheetRows}</sheetData>` +
      hyperlinksXml + '</worksheet>';
    const ssXml =
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      `<sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${strings.length}" uniqueCount="${strings.length}">` +
      strings.map(s => `<si><t xml:space="preserve">${this.escapeXml(s)}</t></si>`).join('') +
      '</sst>';
    const stylesXml =
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
      '<fonts count="4">' +
      '<font><sz val="10"/><name val="Calibri"/><color rgb="FF1A1A2E"/></font>' +
      '<font><sz val="10"/><b/><name val="Calibri"/><color rgb="FF2D4A6B"/></font>' +
      '<font><sz val="10"/><u/><name val="Calibri"/><color rgb="FF1A5EAF"/></font>' +
      '<font><sz val="10"/><b/><name val="Calibri"/><color rgb="FF1A1A2E"/></font>' +
      '</fonts>' +
      '<fills count="6">' +
      '<fill><patternFill patternType="none"/></fill>' +
      '<fill><patternFill patternType="gray125"/></fill>' +
      '<fill><patternFill patternType="solid"><fgColor rgb="FFE8EDF5"/><bgColor indexed="64"/></patternFill></fill>' +
      '<fill><patternFill patternType="solid"><fgColor rgb="FFF5F5F8"/><bgColor indexed="64"/></patternFill></fill>' +
      '<fill><patternFill patternType="solid"><fgColor rgb="FFFFFFFF"/><bgColor indexed="64"/></patternFill></fill>' +
      '<fill><patternFill patternType="solid"><fgColor rgb="FFE8EDF5"/><bgColor indexed="64"/></patternFill></fill>' +
      '</fills>' +
      '<borders count="2">' +
      '<border><left/><right/><top/><bottom/><diagonal/></border>' +
      '<border>' +
      '<left style="thin"><color rgb="FFE0E3EA"/></left>' +
      '<right style="thin"><color rgb="FFE0E3EA"/></right>' +
      '<top style="thin"><color rgb="FFE0E3EA"/></top>' +
      '<bottom style="thin"><color rgb="FFE0E3EA"/></bottom>' +
      '<diagonal/>' +
      '</border>' +
      '</borders>' +
      '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
      '<cellXfs count="5">' +
      '<xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="center" wrapText="1"/></xf>' +
      '<xf numFmtId="0" fontId="0" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="top" wrapText="1"/></xf>' +
      '<xf numFmtId="0" fontId="0" fillId="4" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="top" wrapText="1"/></xf>' +
      '<xf numFmtId="0" fontId="2" fillId="4" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="top" wrapText="1"/></xf>' +
      '<xf numFmtId="0" fontId="3" fillId="5" borderId="0" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="top" wrapText="1"/></xf>' +
      '</cellXfs>' +
      '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
      '</styleSheet>';
    const sheetRelsXml =
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
      '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" Target="../sharedStrings.xml"/>' +
      '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="../styles.xml"/>' +
      hyperlinkRels.map(r =>
        `<Relationship Id="${r.id}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/hyperlink" Target="${this.escapeXml(r.url)}" TargetMode="External"/>`,
      ).join('') +
      '</Relationships>';
    return {
      '[Content_Types].xml':
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
        '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
        '<Override PartName="/xl/sharedStrings.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml"/>' +
        '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
        '</Types>',
      '_rels/.rels':
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
        '</Relationships>',
      'xl/workbook.xml':
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"' +
        ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
        '<sheets><sheet name="Respuesta" sheetId="1" r:id="rId1"/></sheets></workbook>',
      'xl/_rels/workbook.xml.rels':
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
        '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" Target="sharedStrings.xml"/>' +
        '<Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
        '</Relationships>',
      'xl/worksheets/sheet1.xml': sheetXml,
      'xl/worksheets/_rels/sheet1.xml.rels': sheetRelsXml,
      'xl/sharedStrings.xml': ssXml,
      'xl/styles.xml': stylesXml,
    };
  }

  private zipToBlob(files: Record<string, string>): Blob {
    const enc = new TextEncoder();
    const parts: Uint8Array[] = [], central: Uint8Array[] = [];
    let offset = 0;
    const u16 = (n: number) => [(n & 0xff), (n >> 8) & 0xff];
    const u32 = (n: number) => [(n & 0xff), (n >> 8) & 0xff, (n >> 16) & 0xff, (n >> 24) & 0xff];
    const crc32Table = (() => {
      const t = new Uint32Array(256);
      for (let i = 0; i < 256; i++) { let c = i; for (let j = 0; j < 8; j++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[i] = c; }
      return t;
    })();
    const crc32 = (d: Uint8Array) => { let c = 0xffffffff; for (let i = 0; i < d.length; i++) c = crc32Table[(c ^ d[i]) & 0xff] ^ (c >>> 8); return (c ^ 0xffffffff) >>> 0; };
    for (const [name, content] of Object.entries(files)) {
      const nb = enc.encode(name), db = enc.encode(content);
      const crc = crc32(db), size = db.length;
      const local = new Uint8Array([0x50, 0x4b, 0x03, 0x04, 20, 0, 0, 0, 0, 0, 0, 0, 0, 0, ...u32(crc), ...u32(size), ...u32(size), ...u16(nb.length), 0, 0, ...nb]);
      const ce = new Uint8Array([0x50, 0x4b, 0x01, 0x02, 20, 0, 20, 0, 0, 0, 0, 0, 0, 0, 0, 0, ...u32(crc), ...u32(size), ...u32(size), ...u16(nb.length), 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, ...u32(offset), ...nb]);
      parts.push(local, db); central.push(ce);
      offset += local.length + db.length;
    }
    const cb = central.reduce((a, b) => { const r = new Uint8Array(a.length + b.length); r.set(a); r.set(b, a.length); return r; }, new Uint8Array(0));
    const eocd = new Uint8Array([0x50, 0x4b, 0x05, 0x06, 0, 0, 0, 0, ...u16(central.length), ...u16(central.length), ...u32(cb.length), ...u32(offset), 0, 0]);
    const all = [...parts, cb, eocd];
    const buf = new Uint8Array(all.reduce((s, p) => s + p.length, 0));
    let pos = 0; for (const p of all) { buf.set(p, pos); pos += p.length; }
    return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }

  private colLetter(ci: number): string {
    let s = '', n = ci;
    do { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; } while (n >= 0);
    return s;
  }
}
